import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { IoArrowBack } from "react-icons/io5";
import {
  getActionItemPerformerConfig,
  patchActionItemPerformerConfig,
} from "../businessModeService";
import DataSourcesView from "./DataSourcesView";
import BusinessAppSettingsChrome from "./BusinessAppSettingsChrome";
import useSettingsNavCollapsed from "./useSettingsNavCollapsed";
import BusinessAppSettingsNav from "./BusinessAppSettingsNav";
import ActionItemsSettingsGeneralPanel from "./ActionItemsSettingsGeneralPanel";
import ActionItemsSettingsKeyboardPanel from "./ActionItemsSettingsKeyboardPanel";
import ActionItemsSettingsDangerSection from "./ActionItemsSettingsDangerSection";
import ActionItemsSettingsProfileSection from "./ActionItemsSettingsProfileSection";
import PerformerSetupContent from "./PerformerSetupContent";
import {
  ACTION_ITEM_CATEGORY_CATALOG_UPDATED_EVENT,
} from "./actionItemCategories";
import {
  ACTION_ITEMS_SETTINGS_TAB_CONNECT_APPS,
  ACTION_ITEMS_SETTINGS_TAB_GENERAL,
  ACTION_ITEMS_SETTINGS_TAB_KEYBOARD,
  ACTION_ITEMS_SETTINGS_TAB_ROOT,
} from "./actionItemHelpers";
import {
  ACTION_ITEMS_SETTINGS_GENERAL_SCREEN_HUB,
  resolveGeneralSettingsScreenTitle,
} from "./settingsGeneralHelpers";
import {
  markAiAssistSettingsShown,
  RECIPIENT_PROFILE_CACHE_KEY,
} from "./shared/performerAssistGate";
import {
  composeRecipientProfileText,
  parseRecipientProfileFields,
  RECIPIENT_PROFILE_STYLE_OPTIONS,
} from "./shared/performerRecipientProfile";
import { usePerformerSetup } from "./usePerformerSetup";
import { useAuth } from "../../core/context/AuthContext";
import {
  readConnectorSnapshot,
  writeConnectorSnapshot,
} from "../../core/utils/connectorSnapshotCache";

const EXTENSION_SETTINGS_TAB_OPTIONS = [
  { id: ACTION_ITEMS_SETTINGS_TAB_GENERAL, label: "General" },
  { id: ACTION_ITEMS_SETTINGS_TAB_CONNECT_APPS, label: "Connectors" },
];

/**
 * Maps a performer-config response (or its cached copy) to the settings form fields.
 *
 * @example
 * configToFormState({ recipient_profile: "VP Sales", working_hours_start: 9 });
 * // { urls: [], files: [], catalog: null, profile: "VP Sales", style: ..., hoursStart: 9, hoursEnd: null }
 */
const configToFormState = (data) => {
  const profile = String(data?.recipient_profile || "").trim();
  return {
    urls: Array.isArray(data?.drive_urls) ? data.drive_urls : [],
    files: Array.isArray(data?.context_files) ? data.context_files : [],
    catalog: Array.isArray(data?.category_catalog) ? data.category_catalog : null,
    descriptions:
      data?.category_descriptions && typeof data.category_descriptions === "object"
        ? data.category_descriptions
        : null,
    profile,
    style: parseRecipientProfileFields(profile).style,
    hoursStart: data?.working_hours_start != null ? Number(data.working_hours_start) : null,
    hoursEnd: data?.working_hours_end != null ? Number(data.working_hours_end) : null,
  };
};

/** Debounce window for profile / reference-doc text before PATCH. */
const AUTO_SAVE_DEBOUNCE_MS = 600;

/**
 * Builds performer-config PATCH payload for settings save.
 *
 * @example
 * buildPerformerConfigPatchPayload({
 *   driveUrls: [],
 *   categoryCatalog: null,
 *   recipientProfile: "VP Sales",
 *   recipientProfileDirty: true,
 *   workingHoursStart: 9,
 *   workingHoursEnd: 12,
 *   workingHoursDirty: true,
 * });
 */
const buildPerformerConfigPatchPayload = ({
  driveUrls,
  categoryCatalog,
  categoryDescriptions,
  recipientProfile,
  recipientProfileDirty,
  workingHoursStart,
  workingHoursEnd,
  workingHoursDirty,
}) => ({
  driveUrls,
  categoryCatalog,
  categoryDescriptions,
  recipientProfile: recipientProfileDirty
    ? String(recipientProfile || "").trim()
    : undefined,
  ...(workingHoursDirty
    ? {
        workingHoursStart: workingHoursStart ?? null,
        workingHoursEnd: workingHoursEnd ?? null,
      }
    : {}),
});

/**
 * Settings with General and Connect Apps tabs (page or extension panel).
 *
 * @example
 * <ActionItemsSettings layout="page" activeTab="general" onClose={() => navigate(-1)} />
 */
const ActionItemsSettings = ({
  layout = "panel",
  open = false,
  onClose,
  userName = "",
  userEmail = "",
  setupProfileHighlight = false,
  pendingItems = [],
  aiResultsByItem = {},
  onResultsReady,
  onProgressReady,
  onBulkRunStart,
  onBulkRunFinished,
  onGmailAuthUrl,
  performerScheduleSummary,
  onTogglePerformerSchedules,
  onDeletePerformerSchedules,
  performerScheduleBusy,
  activeTab = ACTION_ITEMS_SETTINGS_TAB_ROOT,
  onTabChange,
  connectAppFocus = "",
  connectWorkflowFocus = "",
  connectFocusPending = false,
  workflows = [],
  workflowsLoading = false,
  connectedWorkflowTypes = [],
  connectAppsVariant = "web",
  settingsVariant = "",
  connectedCrmIds = [],
  crmStatusById = {},
  onCrmStatusChange,
  onConnectAppStartSetup,
  onNavigateToSetup,
  onOpenWorkflow,
  onWorkflowsChange,
  onDeleteAccount,
  onPasteTranscriptInstead,
  onConnectDetailBack,
}) => {
  const isPageLayout = layout === "page";
  const isActive = isPageLayout || open;
  const resolvedSettingsVariant =
    settingsVariant || (connectAppsVariant === "extension" ? "extension" : "web");
  const isExtensionSettings = resolvedSettingsVariant === "extension";

  // Last-known saved config for this user: the form paints it immediately and the fetch below
  // refreshes it quietly, so reopening Settings never flashes "Loading profile…".
  const authUser = useAuth()?.user;
  const userKey = String(authUser?.id || authUser?.email || "").trim();
  const [cachedConfig] = useState(() => readConnectorSnapshot(userKey)?.performerConfig || null);
  const cachedForm = useMemo(
    () => (cachedConfig ? configToFormState(cachedConfig) : null),
    [cachedConfig]
  );
  const lastConfigRef = useRef(cachedConfig);
  const hasConfigRef = useRef(Boolean(cachedConfig));
  const cachedFormAppliedRef = useRef(false);

  const [configLoading, setConfigLoading] = useState(false);
  const [configSaving, setConfigSaving] = useState(false);
  const [configError, setConfigError] = useState("");
  const [configSaved, setConfigSaved] = useState(false);
  const [generalScreen, setGeneralScreen] = useState(ACTION_ITEMS_SETTINGS_GENERAL_SCREEN_HUB);
  const [categoryCatalog, setCategoryCatalog] = useState(cachedForm?.catalog ?? null);
  // label -> optional description; null until the saved config has loaded (never PATCH null).
  const [categoryDescriptions, setCategoryDescriptions] = useState(cachedForm?.descriptions ?? null);
  const [profileDescription, setProfileDescription] = useState(cachedForm?.profile ?? "");
  const [profileDescriptionDirty, setProfileDescriptionDirty] = useState(false);
  const [recipientStyle, setRecipientStyle] = useState(
    cachedForm?.style ?? RECIPIENT_PROFILE_STYLE_OPTIONS[0].id
  );
  const [recipientStyleDirty, setRecipientStyleDirty] = useState(false);
  const [workingHoursStart, setWorkingHoursStart] = useState(cachedForm?.hoursStart ?? null);
  const [workingHoursEnd, setWorkingHoursEnd] = useState(cachedForm?.hoursEnd ?? null);
  const [workingHoursDirty, setWorkingHoursDirty] = useState(false);
  const [connectDetailTitle, setConnectDetailTitle] = useState("");
  const [isNavCollapsed, toggleNavCollapsed] = useSettingsNavCollapsed();
  const dataSourcesRef = useRef(null);
  const configDirtyRef = useRef(false);
  const saveInFlightRef = useRef(false);
  const autoSaveTimerRef = useRef(null);
  const saveSettingsRef = useRef(null);

  const setup = usePerformerSetup({
    pendingItems,
    aiResultsByItem,
    onResultsReady,
    onProgressReady,
    onBulkRunStart,
    onBulkRunFinished,
    onGmailAuthUrl,
    autoCheckGmail: isActive,
    enabled: isActive,
  });

  const { setDriveInput, setContextFiles, parseDriveUrlsFromText } = setup;

  useEffect(() => {
    if (!isActive) return;
    markAiAssistSettingsShown();
  }, [isActive]);

  useEffect(() => {
    if (!isActive) return undefined;
    let cancelled = false;
    setProfileDescriptionDirty(false);
    setRecipientStyleDirty(false);
    setWorkingHoursDirty(false);
    if (cachedForm && !cachedFormAppliedRef.current) {
      cachedFormAppliedRef.current = true;
      setDriveInput(cachedForm.urls.join("\n"));
      setContextFiles(cachedForm.files);
    }
    const loadSavedConfig = async () => {
      // With a known config the fetch is a quiet refresh: no spinner, no dimming.
      const quiet = hasConfigRef.current;
      if (!quiet) setConfigLoading(true);
      setConfigError("");
      try {
        const data = await getActionItemPerformerConfig();
        if (cancelled) return;
        lastConfigRef.current = data;
        writeConnectorSnapshot(userKey, { performerConfig: data });
        hasConfigRef.current = true;
        // A quiet refresh must not overwrite what the user is typing right now.
        if (quiet && configDirtyRef.current) return;
        const form = configToFormState(data);
        setDriveInput(form.urls.join("\n"));
        setContextFiles(form.files);
        setCategoryCatalog(form.catalog);
        setCategoryDescriptions(form.descriptions);
        setProfileDescription(form.profile);
        setRecipientStyle(form.style);
        setWorkingHoursStart(form.hoursStart);
        setWorkingHoursEnd(form.hoursEnd);
        setProfileDescriptionDirty(false);
        setRecipientStyleDirty(false);
        setWorkingHoursDirty(false);
        configDirtyRef.current = false;
      } catch (err) {
        if (!cancelled) {
          setConfigError(String(err?.message || "Failed to load saved settings"));
        }
      } finally {
        if (!cancelled) setConfigLoading(false);
      }
    };
    void loadSavedConfig();
    return () => {
      cancelled = true;
    };
  }, [activeTab, isActive, setDriveInput, setContextFiles, cachedForm, userKey]);

  const persistConfigPatch = useCallback(
    (patch) => {
      const next = { ...(lastConfigRef.current || {}), ...patch };
      lastConfigRef.current = next;
      writeConnectorSnapshot(userKey, { performerConfig: next });
    },
    [userKey]
  );

  useEffect(() => {
    if (!isActive) return undefined;
    const handleCatalogUpdated = (event) => {
      const catalog = event?.detail?.categoryCatalog;
      if (Array.isArray(catalog) && catalog.length) {
        setCategoryCatalog(catalog);
        persistConfigPatch({ category_catalog: catalog });
      }
    };
    window.addEventListener(ACTION_ITEM_CATEGORY_CATALOG_UPDATED_EVENT, handleCatalogUpdated);
    return () => {
      window.removeEventListener(ACTION_ITEM_CATEGORY_CATALOG_UPDATED_EVENT, handleCatalogUpdated);
    };
  }, [isActive, persistConfigPatch]);

  const resolveRecipientProfileForSave = useCallback((overrides = {}) => {
    const description = overrides.profileDescription ?? profileDescription;
    const descriptionDirty =
      overrides.profileDescriptionDirty ?? profileDescriptionDirty;
    const style = overrides.recipientStyle ?? recipientStyle;
    const styleDirty = overrides.recipientStyleDirty ?? recipientStyleDirty;

    if (overrides.recipientStyleDirty || (styleDirty && !descriptionDirty)) {
      const parsed = parseRecipientProfileFields(description);
      const roleText = parsed.role || String(description || "").trim();
      return {
        recipientProfile: composeRecipientProfileText({ role: roleText, style }),
        dirty: true,
      };
    }
    if (descriptionDirty) {
      return { recipientProfile: description, dirty: true };
    }
    return { recipientProfile: "", dirty: false };
  }, [profileDescription, profileDescriptionDirty, recipientStyle, recipientStyleDirty]);

  const saveSettings = useCallback(async ({ overrides = {}, force = false } = {}) => {
    const hasOverrides = Object.keys(overrides).length > 0;
    if (!force && !configDirtyRef.current && !hasOverrides) {
      return true;
    }
    if (saveInFlightRef.current) {
      return false;
    }

    saveInFlightRef.current = true;
    setConfigSaving(true);
    setConfigError("");
    setConfigSaved(false);
    try {
      const driveUrls = parseDriveUrlsFromText(overrides.driveInput ?? setup.driveInput);
      const nextCategoryCatalog =
        overrides.categoryCatalog !== undefined ? overrides.categoryCatalog : categoryCatalog;
      const nextCategoryDescriptions =
        overrides.categoryDescriptions !== undefined
          ? overrides.categoryDescriptions
          : categoryDescriptions;
      const { recipientProfile, dirty } = resolveRecipientProfileForSave(overrides);
      const nextWorkingHoursStart =
        overrides.workingHoursStart !== undefined
          ? overrides.workingHoursStart
          : workingHoursStart;
      const nextWorkingHoursEnd =
        overrides.workingHoursEnd !== undefined ? overrides.workingHoursEnd : workingHoursEnd;
      const hoursDirty =
        overrides.workingHoursStart !== undefined ||
        overrides.workingHoursEnd !== undefined ||
        workingHoursDirty;
      await patchActionItemPerformerConfig(
        buildPerformerConfigPatchPayload({
          driveUrls,
          categoryCatalog: nextCategoryCatalog,
          categoryDescriptions: nextCategoryDescriptions,
          recipientProfile,
          recipientProfileDirty: dirty,
          workingHoursStart: nextWorkingHoursStart,
          workingHoursEnd: nextWorkingHoursEnd,
          workingHoursDirty: hoursDirty,
        })
      );
      if (dirty && recipientProfile) {
        localStorage.setItem(RECIPIENT_PROFILE_CACHE_KEY, recipientProfile);
      }
      persistConfigPatch({
        drive_urls: driveUrls,
        category_catalog: nextCategoryCatalog,
        ...(nextCategoryDescriptions ? { category_descriptions: nextCategoryDescriptions } : {}),
        working_hours_start: nextWorkingHoursStart,
        working_hours_end: nextWorkingHoursEnd,
        ...(dirty ? { recipient_profile: recipientProfile } : {}),
      });
      setConfigSaved(true);
      configDirtyRef.current = false;
      setProfileDescriptionDirty(false);
      setRecipientStyleDirty(false);
      setWorkingHoursDirty(false);
      return true;
    } catch (err) {
      setConfigError(String(err?.message || "Failed to save settings"));
      return false;
    } finally {
      saveInFlightRef.current = false;
      setConfigSaving(false);
    }
  }, [
    categoryCatalog,
    categoryDescriptions,
    parseDriveUrlsFromText,
    persistConfigPatch,
    resolveRecipientProfileForSave,
    setup.driveInput,
    workingHoursDirty,
    workingHoursEnd,
    workingHoursStart,
  ]);

  saveSettingsRef.current = saveSettings;

  const flushDebouncedAutoSave = useCallback(async () => {
    if (autoSaveTimerRef.current) {
      window.clearTimeout(autoSaveTimerRef.current);
      autoSaveTimerRef.current = null;
    }
    if (!configDirtyRef.current) return true;
    return saveSettings();
  }, [saveSettings]);

  const scheduleDebouncedAutoSave = useCallback(() => {
    if (autoSaveTimerRef.current) {
      window.clearTimeout(autoSaveTimerRef.current);
    }
    autoSaveTimerRef.current = window.setTimeout(() => {
      autoSaveTimerRef.current = null;
      void saveSettings();
    }, AUTO_SAVE_DEBOUNCE_MS);
  }, [saveSettings]);

  const handleDismiss = useCallback(async () => {
    if (activeTab === ACTION_ITEMS_SETTINGS_TAB_GENERAL) {
      await flushDebouncedAutoSave();
    }
    onClose?.();
  }, [activeTab, flushDebouncedAutoSave, onClose]);

  const handleSkipProfileSetup = useCallback(() => {
    onClose?.();
  }, [onClose]);

  const markConfigDirty = useCallback(() => {
    configDirtyRef.current = true;
    setConfigSaved(false);
  }, []);

  const handleProfileDescriptionChange = useCallback(
    (value) => {
      setProfileDescription(value);
      setProfileDescriptionDirty(true);
      markConfigDirty();
      scheduleDebouncedAutoSave();
    },
    [markConfigDirty, scheduleDebouncedAutoSave]
  );

  const handleProfileDescriptionBlur = useCallback(() => {
    void flushDebouncedAutoSave();
  }, [flushDebouncedAutoSave]);

  const handleContextEdited = useCallback(() => {
    markConfigDirty();
    scheduleDebouncedAutoSave();
  }, [markConfigDirty, scheduleDebouncedAutoSave]);

  const handleCategoryCatalogChange = useCallback(
    (next, nextDescriptions) => {
      setCategoryCatalog(next);
      if (nextDescriptions) setCategoryDescriptions(nextDescriptions);
      markConfigDirty();
      void saveSettings({
        overrides: {
          categoryCatalog: next,
          ...(nextDescriptions ? { categoryDescriptions: nextDescriptions } : {}),
        },
        force: true,
      });
    },
    [markConfigDirty, saveSettings]
  );

  const handleCategoryDescriptionsChange = useCallback(
    (next) => {
      setCategoryDescriptions(next);
      markConfigDirty();
      void saveSettings({ overrides: { categoryDescriptions: next }, force: true });
    },
    [markConfigDirty, saveSettings]
  );

  const handleRecipientStyleChange = useCallback(
    (styleId) => {
      setRecipientStyle(styleId);
      setRecipientStyleDirty(true);
      markConfigDirty();
      void saveSettings({
        overrides: { recipientStyle: styleId, recipientStyleDirty: true },
        force: true,
      });
    },
    [markConfigDirty, saveSettings]
  );

  const handleMeetingPreferencesChange = useCallback(
    ({ workingHoursStart: nextStart, workingHoursEnd: nextEnd }) => {
      setWorkingHoursStart(nextStart ?? null);
      setWorkingHoursEnd(nextEnd ?? null);
      setWorkingHoursDirty(true);
      markConfigDirty();
      void saveSettings({
        overrides: {
          workingHoursStart: nextStart ?? null,
          workingHoursEnd: nextEnd ?? null,
        },
        force: true,
      });
    },
    [markConfigDirty, saveSettings]
  );

  const handleDriveInputBlur = useCallback(() => {
    void flushDebouncedAutoSave();
  }, [flushDebouncedAutoSave]);

  useEffect(() => {
    if (!configSaved) return undefined;
    const timer = window.setTimeout(() => setConfigSaved(false), 3000);
    return () => window.clearTimeout(timer);
  }, [configSaved]);

  const effectiveActiveTab =
    isPageLayout && activeTab === ACTION_ITEMS_SETTINGS_TAB_ROOT
      ? ACTION_ITEMS_SETTINGS_TAB_GENERAL
      : activeTab;
  const isGeneralTab = effectiveActiveTab === ACTION_ITEMS_SETTINGS_TAB_GENERAL;
  const isConnectTab = effectiveActiveTab === ACTION_ITEMS_SETTINGS_TAB_CONNECT_APPS;
  const isKeyboardTab = effectiveActiveTab === ACTION_ITEMS_SETTINGS_TAB_KEYBOARD;
  const isRoot = !isGeneralTab && !isConnectTab && !isKeyboardTab;
  const isExtensionGeneralHub =
    isExtensionSettings &&
    isGeneralTab &&
    generalScreen === ACTION_ITEMS_SETTINGS_GENERAL_SCREEN_HUB;
  const isExtensionGeneralDrillDown =
    isExtensionSettings && isGeneralTab && generalScreen !== ACTION_ITEMS_SETTINGS_GENERAL_SCREEN_HUB;

  const headingTitle = isPageLayout
    ? isConnectTab
      ? "Connectors"
      : isKeyboardTab
        ? "Keyboard shortcuts"
        : "General"
    : isRoot
      ? "Settings"
      : isGeneralTab
        ? isExtensionGeneralDrillDown
          ? resolveGeneralSettingsScreenTitle(generalScreen)
          : "General"
        : isKeyboardTab
          ? "Keyboard shortcuts"
          : connectDetailTitle || "Connectors";

  const handleConnectDetailBack = useCallback(() => {
    onConnectDetailBack?.();
    dataSourcesRef.current?.closeDetail();
  }, [onConnectDetailBack]);

  const handleBackToTasks = useCallback(() => {
    if (isGeneralTab) {
      void flushDebouncedAutoSave().then(() => {
        handleDismiss();
      });
      return;
    }
    void handleDismiss();
  }, [flushDebouncedAutoSave, handleDismiss, isGeneralTab]);

  const handleTabChange = useCallback(
    (nextTab) => {
      if (isGeneralTab && nextTab !== ACTION_ITEMS_SETTINGS_TAB_GENERAL) {
        void flushDebouncedAutoSave().then(() => {
          onTabChange?.(nextTab);
        });
        return;
      }
      onTabChange?.(nextTab);
    },
    [flushDebouncedAutoSave, isGeneralTab, onTabChange]
  );

  useEffect(() => {
    return () => {
      if (autoSaveTimerRef.current) {
        window.clearTimeout(autoSaveTimerRef.current);
        autoSaveTimerRef.current = null;
      }
      if (configDirtyRef.current && typeof saveSettingsRef.current === "function") {
        void saveSettingsRef.current();
      }
    };
  }, []);

  useEffect(() => {
    if (!isGeneralTab) {
      setGeneralScreen(ACTION_ITEMS_SETTINGS_GENERAL_SCREEN_HUB);
    }
  }, [isGeneralTab]);

  const rootListClass = isPageLayout
    ? "business-app-settings__root-list"
    : "ai-drafts-panel__root-list";
  const rootItemClass = isPageLayout
    ? "business-app-settings__root-item"
    : "ai-drafts-panel__root-item";
  const bodyClass = isPageLayout ? "business-app-settings__body" : "ai-drafts-panel__body";
  const panelClass = isPageLayout ? "business-app-settings__panel" : "ai-drafts-panel__tabpanel";
  const generalPanelClass = isPageLayout
    ? "business-app-settings__panel business-app-settings__form-width"
    : panelClass;

  const statusNotice = (
    <>
      {configError ? <p className="ai-drafts-panel__notice">{configError}</p> : null}
      {configSaving ? <p className="ai-drafts-panel__status">Saving…</p> : null}
      {configSaved ? (
        <p className="ai-drafts-panel__status ai-drafts-panel__status--ok">Settings saved.</p>
      ) : null}
    </>
  );

  const generalPanel = isPageLayout ? (
    <div className={generalPanelClass}>
      {configLoading && !isExtensionSettings ? (
        <p className="ai-drafts-panel__hint">Loading saved context…</p>
      ) : null}
      <ActionItemsSettingsGeneralPanel
        variant={resolvedSettingsVariant}
        generalScreen={generalScreen}
        userName={userName}
        userEmail={userEmail}
        profileDescription={profileDescription}
        profileLoading={configLoading}
        onProfileDescriptionChange={handleProfileDescriptionChange}
        onProfileDescriptionBlur={handleProfileDescriptionBlur}
        setupProfileHighlight={setupProfileHighlight}
        onSkipProfileSetup={setupProfileHighlight ? handleSkipProfileSetup : undefined}
        setup={setup}
        pendingCount={pendingItems.length}
        categoryCatalog={categoryCatalog}
        onCategoryCatalogChange={handleCategoryCatalogChange}
        categoryDescriptions={categoryDescriptions}
        onCategoryDescriptionsChange={handleCategoryDescriptionsChange}
        onContextEdited={handleContextEdited}
        onDriveInputBlur={handleDriveInputBlur}
        recipientStyle={recipientStyle}
        onRecipientStyleChange={handleRecipientStyleChange}
        workingHoursStart={workingHoursStart}
        workingHoursEnd={workingHoursEnd}
        onMeetingPreferencesChange={handleMeetingPreferencesChange}
        onDeleteAccount={onDeleteAccount}
        configLoading={configLoading}
        onHubNavigate={setGeneralScreen}
      />
    </div>
  ) : (
    <div className={generalPanelClass}>
      <ActionItemsSettingsProfileSection
        userName={userName}
        userEmail={userEmail}
        profileDescription={profileDescription}
        profileLoading={configLoading}
        onProfileDescriptionChange={handleProfileDescriptionChange}
        onProfileDescriptionBlur={handleProfileDescriptionBlur}
        setupProfileHighlight={setupProfileHighlight}
        onSkipProfileSetup={setupProfileHighlight ? handleSkipProfileSetup : undefined}
      />
      {configLoading ? <p className="ai-drafts-panel__hint">Loading saved context…</p> : null}
      <div className={configLoading ? "business-app-settings-block__content--dimmed" : ""}>
        <PerformerSetupContent
          setup={setup}
          pendingCount={pendingItems.length}
          showRunControls={false}
          showCategorySection
          compactCopy
          categoryCatalog={categoryCatalog}
          onCategoryCatalogChange={handleCategoryCatalogChange}
          categoryDescriptions={categoryDescriptions}
          onCategoryDescriptionsChange={handleCategoryDescriptionsChange}
          onContextEdited={handleContextEdited}
          onDriveInputBlur={handleDriveInputBlur}
        />
        <div className="ai-drafts-panel__field-row">
          <span className="ai-drafts-panel__field-label">Draft style</span>
          <div className="ai-drafts-panel__style-toggle" role="group" aria-label="Draft writing style">
            {RECIPIENT_PROFILE_STYLE_OPTIONS.map((option) => {
              const active = recipientStyle === option.id;
              return (
                <button
                  key={option.id}
                  type="button"
                  className={`ai-drafts-panel__style-toggle-btn${
                    active ? " ai-drafts-panel__style-toggle-btn--active" : ""
                  }`}
                  aria-pressed={active}
                  onClick={() => handleRecipientStyleChange(option.id)}
                >
                  <span className="ai-drafts-panel__style-toggle-label">{option.label}</span>
                  <span className="ai-drafts-panel__style-toggle-desc">{option.description}</span>
                </button>
              );
            })}
          </div>
        </div>
        <ActionItemsSettingsDangerSection
          userEmail={userEmail}
          onDeleteAccount={onDeleteAccount}
          compact
        />
      </div>
    </div>
  );

  const connectPanel = (
    <div className={panelClass}>
      <DataSourcesView
        ref={dataSourcesRef}
        variant={connectAppsVariant}
        focusAppId={connectAppFocus}
        focusWorkflowId={connectWorkflowFocus}
        focusPending={connectFocusPending}
        workflows={workflows}
        workflowsLoading={workflowsLoading}
        connectedWorkflowTypes={connectedWorkflowTypes}
        connectedCrmIds={connectedCrmIds}
        crmStatusById={crmStatusById}
        onCrmStatusChange={onCrmStatusChange}
        onStartSetup={onConnectAppStartSetup}
        onNavigateToSetup={onNavigateToSetup}
        onWorkflowsChange={onWorkflowsChange}
        onDetailStateChange={(open, title) => {
          setConnectDetailTitle(open ? title : "");
        }}
        onPasteTranscriptInstead={onPasteTranscriptInstead}
        onGoToTasks={typeof onClose === "function" ? onClose : undefined}
      />
    </div>
  );

  const keyboardPanel = (
    <div className={generalPanelClass}>
      <ActionItemsSettingsKeyboardPanel />
    </div>
  );

  const generalAutoSaveStatusVisible =
    configSaving || configSaved || Boolean(configError);

  const autoSaveFooter =
    isGeneralTab && !isExtensionGeneralHub && generalAutoSaveStatusVisible ? (
    <footer
      className={
        isPageLayout
          ? "business-app-settings__footer business-app-settings__form-width business-app-settings__footer--autosave"
          : "ai-drafts-panel__footer ai-drafts-panel__footer--autosave"
      }
    >
      {statusNotice}
    </footer>
  ) : null;

  const rootList = (
    <div className={rootListClass} role="list" aria-label="Settings sections">
      {EXTENSION_SETTINGS_TAB_OPTIONS.map((tab) => (
        <button
          key={tab.id}
          type="button"
          className={rootItemClass}
          onClick={() => handleTabChange?.(tab.id)}
        >
          <span>{tab.label}</span>
          <span className="business-app-settings__root-item-chevron" aria-hidden="true">
            ›
          </span>
        </button>
      ))}
    </div>
  );

  const settingsBody =
    isPageLayout || !isRoot ? (
      <>
        <div className={bodyClass}>
          {isGeneralTab ? generalPanel : null}
          {isConnectTab ? connectPanel : null}
          {isKeyboardTab ? keyboardPanel : null}
        </div>
        {autoSaveFooter}
      </>
    ) : (
      <div className={isPageLayout ? "business-app-settings__body" : ""}>{rootList}</div>
    );

  if (!isPageLayout && !open) return null;

  if (isPageLayout) {
    const showConnectDetailBack = isConnectTab && Boolean(connectDetailTitle);

    const pageHeader = (
      <div className="business-wizard-header">
        {showConnectDetailBack ? (
          <button
            type="button"
            className="business-wizard-back-btn"
            onClick={handleConnectDetailBack}
            aria-label="Back to connectors"
          >
            <IoArrowBack aria-hidden="true" />
          </button>
        ) : null}
        <div className="business-wizard-header-content">
          <h2 className="business-wizard-title">{headingTitle}</h2>
        </div>
      </div>
    );

    return (
      <div className="business-wizard-wrap business-wizard-wrap--app-settings">
        <div className="business-app-settings-shell">
          <BusinessAppSettingsChrome
            isNavCollapsed={isNavCollapsed}
            onToggleNav={toggleNavCollapsed}
            onBackHome={handleBackToTasks}
          />
          <div
            className={`business-app-settings-layout${
              isNavCollapsed ? " business-app-settings-layout--nav-collapsed" : ""
            }`}
          >
            <BusinessAppSettingsNav
              activeTab={effectiveActiveTab}
              onTabChange={handleTabChange}
              isCollapsed={isNavCollapsed}
            />
            <div className="business-app-settings-main">
              <div className="business-settings-header business-app-settings-topbar">
                {pageHeader}
              </div>
              <div className="business-app-settings">{settingsBody}</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="ai-drafts-panel-backdrop" onClick={() => void handleDismiss()} aria-hidden="true" />
      <aside
        className="ai-drafts-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="ai-drafts-panel-title"
      >
        <header className="ai-drafts-panel__header">
          <div className="ai-drafts-panel__head-text">
            <h2 id="ai-drafts-panel-title" className="ai-drafts-panel__title">
              Settings
            </h2>
          </div>
          <button
            type="button"
            className="performer-overlay__close"
            onClick={() => void handleDismiss()}
            aria-label="Close settings"
          >
            ✕
          </button>
        </header>
        {settingsBody}
      </aside>
    </>
  );
};

export default ActionItemsSettings;
